/**
 * 2D navigation grid overlaid on a single walkable surface.
 * Cells are marked as walkable or blocked based on obstacles above the surface.
 */
export default class SurfaceGrid {
  // Default grid cell size in world units
  static CELL_SIZE = 0.5;

  // Character dimensions for clearance checks (from NavMeshBuilder)
  static CHARACTER_WIDTH = 0.9;
  static CHARACTER_HEIGHT = 2.0;
  static CHARACTER_DEPTH = 0.9;

  // Grid probe MUST match character size for accurate walkability
  // A cell is walkable only if the character can actually stand there
  static GRID_PROBE_WIDTH = 0.9;
  static GRID_PROBE_DEPTH = 0.9;

  // Corner margin - extra clearance when checking diagonal movement
  static CORNER_MARGIN = 0.45;

  // Wall margin - extra clearance from vertical obstacles (walls)
  // This prevents the character from walking too close to walls
  static WALL_MARGIN = 0.15;

  // Edge margin - how close to surface edge is still walkable
  // Prevents walking too close to ledges/drops
  // Must be large enough to account for character width and movement smoothing
  static EDGE_MARGIN = 0.7;

  /**
   * Create a surface grid
   * @param {NavNode} navNode - The navigation node this grid belongs to
   * @param {CharacterCollisionSystem} collisionSystem - For obstacle queries
   * @param {number} cellSize - Size of each grid cell
   */
  constructor(navNode, collisionSystem, cellSize = SurfaceGrid.CELL_SIZE) {
    this.navNode = navNode;
    this.collisionSystem = collisionSystem;
    this.cellSize = cellSize;

    // Surface bounds
    this.minX = navNode.minX;
    this.maxX = navNode.maxX;
    this.minZ = navNode.minZ;
    this.maxZ = navNode.maxZ;
    this.surfaceY = navNode.y;

    // Grid dimensions
    this.width = Math.ceil((this.maxX - this.minX) / cellSize);
    this.height = Math.ceil((this.maxZ - this.minZ) / cellSize);

    // 2D array: true = walkable, false = blocked
    // Indexed as cells[x][z]
    this.cells = [];

    // Pre-computed diagonal blocking: Map<"cx,cz,dx,dz" -> boolean>
    // Computed once during build() to avoid expensive collision queries during pathfinding
    this.diagonalBlocked = new Map();

    // Cache neighbor results for performance
    this.neighborsCache = new Map();

    this.build();
  }

  /**
   * Build the grid by checking each cell for obstacles
   * Uses full character dimensions for accurate walkability
   */
  build() {
    this.cells = [];
    this.neighborsCache.clear();
    this.diagonalBlocked.clear();

    // Use character-sized probe for accurate walkability
    const halfProbeWidth = SurfaceGrid.GRID_PROBE_WIDTH / 2;
    const halfProbeDepth = SurfaceGrid.GRID_PROBE_DEPTH / 2;
    const wallMargin = SurfaceGrid.WALL_MARGIN;
    const edgeMargin = SurfaceGrid.EDGE_MARGIN;

    // Scale edge margin down for small surfaces to guarantee walkable cells exist.
    // For a 1x1 surface with full EDGE_MARGIN=0.7, ALL cells are blocked (no room).
    // This ensures the center cell(s) always pass the edge check.
    const surfaceWidth = this.maxX - this.minX;
    const surfaceDepth = this.maxZ - this.minZ;
    const edgeMarginX = Math.min(edgeMargin, Math.max(0.1, surfaceWidth / 2 - this.cellSize * 0.5));
    const edgeMarginZ = Math.min(edgeMargin, Math.max(0.1, surfaceDepth / 2 - this.cellSize * 0.5));

    for (let cx = 0; cx < this.width; cx++) {
      this.cells[cx] = [];
      for (let cz = 0; cz < this.height; cz++) {
        // Get world position of cell center
        const worldX = this.minX + (cx + 0.5) * this.cellSize;
        const worldZ = this.minZ + (cz + 0.5) * this.cellSize;

        // Check if cell is too close to surface edge
        const distToMinX = worldX - this.minX;
        const distToMaxX = this.maxX - worldX;
        const distToMinZ = worldZ - this.minZ;
        const distToMaxZ = this.maxZ - worldZ;

        if (distToMinX < edgeMarginX || distToMaxX < edgeMarginX ||
            distToMinZ < edgeMarginZ || distToMaxZ < edgeMarginZ) {
          this.cells[cx][cz] = false;
          continue;
        }

        // First check: verify there's actually a floor at this position
        // This detects holes in the surface
        if (!this._hasFloorAt(worldX, worldZ)) {
          this.cells[cx][cz] = false;
          continue;
        }

        // Query for obstacles with character size + wall margin
        // This ensures the character won't clip walls when walking
        const queryBox = {
          minX: worldX - halfProbeWidth - wallMargin,
          maxX: worldX + halfProbeWidth + wallMargin,
          minY: this.surfaceY + 0.01, // Just above surface
          maxY: this.surfaceY + SurfaceGrid.CHARACTER_HEIGHT,
          minZ: worldZ - halfProbeDepth - wallMargin,
          maxZ: worldZ + halfProbeDepth + wallMargin
        };

        const obstacles = this.collisionSystem.queryCuboids(queryBox);

        // Check if any obstacle actually blocks this cell
        let isBlocked = false;
        for (const obstacle of obstacles) {
          // Skip non-collider cuboids (water, gas, etc.)
          if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

          const obsTop = obstacle.y + obstacle.height;
          const obsBottom = obstacle.y;

          // Skip if this is the floor we're standing on
          // (obstacle's top surface IS our walking surface)
          if (Math.abs(obsTop - this.surfaceY) < 0.1) continue;

          // Skip if obstacle is entirely below our surface
          if (obsTop <= this.surfaceY + 0.01) continue;

          // Check if obstacle occupies head space
          if (obsBottom < this.surfaceY + SurfaceGrid.CHARACTER_HEIGHT) {
            const obsMinX = obstacle.x;
            const obsMaxX = obstacle.x + obstacle.width;
            const obsMinZ = obstacle.z;
            const obsMaxZ = obstacle.z + obstacle.depth;

            // Step-up obstacles (resting on surface, climbable height) are treated
            // differently: only block if the cell CENTER is inside the obstacle.
            // The character can stand adjacent to a step-up and transition onto it,
            // so we don't need wall margin or character-width avoidance for these.
            const isStepUp = obsBottom <= this.surfaceY + 0.1 &&
                             obsTop <= this.surfaceY + 1.5;

            if (isStepUp) {
              // For step-ups, only block cells whose center is inside the obstacle
              if (worldX >= obsMinX && worldX < obsMaxX &&
                  worldZ >= obsMinZ && worldZ < obsMaxZ) {
                isBlocked = true;
                break;
              }
            } else {
              // For walls/ceilings, use full probe with wall margin
              if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
                  queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
                isBlocked = true;
                break;
              }
            }
          }
        }

        this.cells[cx][cz] = !isBlocked;
      }
    }

    // Pre-compute diagonal blocking for all walkable cells
    // This avoids expensive collision queries during pathfinding
    this._precomputeDiagonalBlocking();
  }

  /**
   * Check if there's a floor surface at this position
   * Detects holes in the walkable surface
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {boolean} True if there's a supporting floor
   * @private
   */
  _hasFloorAt(worldX, worldZ) {
    // Query for cuboids that could be the floor
    const queryBox = {
      minX: worldX - 0.1,
      maxX: worldX + 0.1,
      minY: this.surfaceY - 0.5,
      maxY: this.surfaceY + 0.5,
      minZ: worldZ - 0.1,
      maxZ: worldZ + 0.1
    };

    const cuboids = this.collisionSystem.queryCuboids(queryBox);

    for (const cuboid of cuboids) {
      // Skip non-collider cuboids (water, gas, etc.) - can't be floors
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidTop = cuboid.y + cuboid.height;
      // Check if this cuboid's top surface is at our walking height
      if (Math.abs(cuboidTop - this.surfaceY) < 0.3) {
        // And the point is within the cuboid's XZ bounds
        if (worldX >= cuboid.x && worldX <= cuboid.x + cuboid.width &&
            worldZ >= cuboid.z && worldZ <= cuboid.z + cuboid.depth) {
          return true; // Found supporting floor
        }
      }
    }

    return false; // No floor - there's a hole here
  }

  /**
   * Pre-compute which diagonal moves are blocked by obstacles
   * Called once during build() to cache results for fast pathfinding
   * @private
   */
  _precomputeDiagonalBlocking() {
    const diagonals = [
      { dx: -1, dz: -1 }, // Southwest
      { dx: -1, dz: 1 },  // Northwest
      { dx: 1, dz: -1 },  // Southeast
      { dx: 1, dz: 1 }    // Northeast
    ];

    for (let cx = 0; cx < this.width; cx++) {
      for (let cz = 0; cz < this.height; cz++) {
        // Only check walkable cells
        if (!this.cells[cx][cz]) continue;

        for (const { dx, dz } of diagonals) {
          const nx = cx + dx;
          const nz = cz + dz;

          // Skip if target cell is out of bounds or unwalkable
          if (!this.inBounds(nx, nz) || !this.cells[nx][nz]) continue;

          // Skip if cardinal neighbors aren't both walkable
          if (!this.isWalkable(cx + dx, cz) || !this.isWalkable(cx, cz + dz)) continue;

          // Now do the expensive check once and cache it
          const blocked = this._checkDiagonalBlocked(cx, cz, dx, dz);
          const key = `${cx},${cz},${dx},${dz}`;
          this.diagonalBlocked.set(key, blocked);
        }
      }
    }
  }

  /**
   * Convert world coordinates to cell indices
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {{x: number, z: number}} Cell indices
   */
  worldToCell(worldX, worldZ) {
    const cellX = Math.floor((worldX - this.minX) / this.cellSize);
    const cellZ = Math.floor((worldZ - this.minZ) / this.cellSize);
    return {
      x: Math.max(0, Math.min(this.width - 1, cellX)),
      z: Math.max(0, Math.min(this.height - 1, cellZ))
    };
  }

  /**
   * Convert cell indices to world coordinates (cell center)
   * @param {number} cellX - Cell X index
   * @param {number} cellZ - Cell Z index
   * @returns {{x: number, y: number, z: number}} World position
   */
  cellToWorld(cellX, cellZ) {
    return {
      x: this.minX + (cellX + 0.5) * this.cellSize,
      y: this.surfaceY,
      z: this.minZ + (cellZ + 0.5) * this.cellSize
    };
  }

  /**
   * Check if a cell is within grid bounds
   * @param {number} cellX - Cell X index
   * @param {number} cellZ - Cell Z index
   * @returns {boolean}
   */
  inBounds(cellX, cellZ) {
    return cellX >= 0 && cellX < this.width && cellZ >= 0 && cellZ < this.height;
  }

  /**
   * Check if a cell is walkable
   * @param {number} cellX - Cell X index
   * @param {number} cellZ - Cell Z index
   * @returns {boolean}
   */
  isWalkable(cellX, cellZ) {
    if (!this.inBounds(cellX, cellZ)) return false;
    return this.cells[cellX][cellZ];
  }

  /**
   * Get walkable neighbor cells (8-directional)
   * @param {number} cellX - Cell X index
   * @param {number} cellZ - Cell Z index
   * @returns {Array<{x: number, z: number, cost: number}>} Array of neighbor cells with costs
   */
  getNeighbors(cellX, cellZ) {
    const cacheKey = `${cellX},${cellZ}`;
    if (this.neighborsCache.has(cacheKey)) {
      return this.neighborsCache.get(cacheKey);
    }

    const neighbors = [];
    const SQRT2 = Math.SQRT2;
    const WALL_ADJACENCY_COST = 0.3; // Extra cost for cells near walls

    // 8-directional neighbors: cardinal + diagonal
    const directions = [
      { dx: -1, dz: 0, cost: 1 },        // West
      { dx: 1, dz: 0, cost: 1 },         // East
      { dx: 0, dz: -1, cost: 1 },        // South
      { dx: 0, dz: 1, cost: 1 },         // North
      { dx: -1, dz: -1, cost: SQRT2 },   // Southwest
      { dx: -1, dz: 1, cost: SQRT2 },    // Northwest
      { dx: 1, dz: -1, cost: SQRT2 },    // Southeast
      { dx: 1, dz: 1, cost: SQRT2 }      // Northeast
    ];

    for (const dir of directions) {
      const nx = cellX + dir.dx;
      const nz = cellZ + dir.dz;

      if (!this.isWalkable(nx, nz)) continue;

      // For diagonal movement, require BOTH adjacent cardinal cells walkable
      // This prevents corner cutting through obstacles
      if (dir.dx !== 0 && dir.dz !== 0) {
        const cardinalX = this.isWalkable(cellX + dir.dx, cellZ);
        const cardinalZ = this.isWalkable(cellX, cellZ + dir.dz);
        if (!cardinalX || !cardinalZ) continue;

        // Extra check: verify the diagonal path doesn't clip obstacle corners
        if (this._isDiagonalBlocked(cellX, cellZ, dir.dx, dir.dz)) continue;
      }

      // Calculate wall adjacency penalty for destination cell
      // Cells near blocked cells cost more, encouraging paths away from walls
      let cost = dir.cost;
      const wallCount = this._countAdjacentWalls(nx, nz);
      if (wallCount > 0) {
        cost += WALL_ADJACENCY_COST * wallCount;
      }

      neighbors.push({ x: nx, z: nz, cost });
    }

    this.neighborsCache.set(cacheKey, neighbors);
    return neighbors;
  }

  /**
   * Count how many adjacent cells are blocked (walls)
   * @param {number} cellX - Cell X index
   * @param {number} cellZ - Cell Z index
   * @returns {number} Count of adjacent blocked cells
   * @private
   */
  _countAdjacentWalls(cellX, cellZ) {
    let count = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dz === 0) continue;
        const nx = cellX + dx;
        const nz = cellZ + dz;
        if (!this.inBounds(nx, nz) || !this.cells[nx][nz]) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Check if a diagonal move would clip an obstacle corner
   * Uses pre-computed cache for fast lookup during pathfinding
   * @param {number} cellX - Starting cell X
   * @param {number} cellZ - Starting cell Z
   * @param {number} dx - Direction X (-1, 0, or 1)
   * @param {number} dz - Direction Z (-1, 0, or 1)
   * @returns {boolean} True if diagonal is blocked
   * @private
   */
  _isDiagonalBlocked(cellX, cellZ, dx, dz) {
    // Use pre-computed cache for fast lookup
    const key = `${cellX},${cellZ},${dx},${dz}`;
    if (this.diagonalBlocked.has(key)) {
      return this.diagonalBlocked.get(key);
    }

    // Cache miss - compute and store (shouldn't happen normally after build)
    if (!this.collisionSystem) return false;
    const blocked = this._checkDiagonalBlocked(cellX, cellZ, dx, dz);
    this.diagonalBlocked.set(key, blocked);
    return blocked;
  }

  /**
   * Actually check if a diagonal move would clip an obstacle corner
   * Uses simplified collision check - single query at the midpoint instead of 5 queries
   * @param {number} cellX - Starting cell X
   * @param {number} cellZ - Starting cell Z
   * @param {number} dx - Direction X (-1, 0, or 1)
   * @param {number} dz - Direction Z (-1, 0, or 1)
   * @returns {boolean} True if diagonal is blocked
   * @private
   */
  _checkDiagonalBlocked(cellX, cellZ, dx, dz) {
    if (!this.collisionSystem) return false;

    const margin = SurfaceGrid.CORNER_MARGIN;
    const halfWidth = SurfaceGrid.CHARACTER_WIDTH / 2;
    const halfDepth = SurfaceGrid.CHARACTER_DEPTH / 2;

    // Get start and end cell centers
    const startX = this.minX + (cellX + 0.5) * this.cellSize;
    const startZ = this.minZ + (cellZ + 0.5) * this.cellSize;
    const endX = this.minX + (cellX + dx + 0.5) * this.cellSize;
    const endZ = this.minZ + (cellZ + dz + 0.5) * this.cellSize;

    // Check only the corner point (the critical chokepoint) instead of 5 points
    // This reduces collision queries from 5 to 1 while still catching corner clips
    const cornerX = this.minX + (cellX + (dx > 0 ? 1 : 0)) * this.cellSize;
    const cornerZ = this.minZ + (cellZ + (dz > 0 ? 1 : 0)) * this.cellSize;

    // Query a box covering the entire diagonal path including corner
    const queryBox = {
      minX: Math.min(startX, endX) - halfWidth - margin,
      maxX: Math.max(startX, endX) + halfWidth + margin,
      minY: this.surfaceY + 0.01,
      maxY: this.surfaceY + SurfaceGrid.CHARACTER_HEIGHT,
      minZ: Math.min(startZ, endZ) - halfDepth - margin,
      maxZ: Math.max(startZ, endZ) + halfDepth + margin
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsTop = obstacle.y + obstacle.height;
      const obsBottom = obstacle.y;

      // Skip floor
      if (Math.abs(obsTop - this.surfaceY) < 0.1) continue;
      if (obsTop <= this.surfaceY + 0.01) continue;

      // Check if obstacle would block passage
      if (obsBottom < this.surfaceY + SurfaceGrid.CHARACTER_HEIGHT) {
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        // Step-up obstacles use tighter checks (no margin expansion)
        const isStepUp = obsBottom <= this.surfaceY + 0.1 &&
                         obsTop <= this.surfaceY + 1.5;

        if (isStepUp) {
          // For step-ups, only block diagonal if corner/midpoint is inside the obstacle
          if (cornerX >= obsMinX && cornerX <= obsMaxX &&
              cornerZ >= obsMinZ && cornerZ <= obsMaxZ) {
            return true;
          }
          const midX = (startX + endX) / 2;
          const midZ = (startZ + endZ) / 2;
          if (midX >= obsMinX && midX <= obsMaxX &&
              midZ >= obsMinZ && midZ <= obsMaxZ) {
            return true;
          }
        } else {
          // For walls/ceilings, use full margin checks
          if (cornerX >= obsMinX - margin && cornerX <= obsMaxX + margin &&
              cornerZ >= obsMinZ - margin && cornerZ <= obsMaxZ + margin) {
            return true;
          }
          const midX = (startX + endX) / 2;
          const midZ = (startZ + endZ) / 2;
          if (midX >= obsMinX - halfWidth && midX <= obsMaxX + halfWidth &&
              midZ >= obsMinZ - halfDepth && midZ <= obsMaxZ + halfDepth) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Get all walkable cells (for debugging)
   * @returns {Array<{x: number, z: number}>}
   */
  getWalkableCells() {
    const walkable = [];
    for (let cx = 0; cx < this.width; cx++) {
      for (let cz = 0; cz < this.height; cz++) {
        if (this.cells[cx][cz]) {
          walkable.push({ x: cx, z: cz });
        }
      }
    }
    return walkable;
  }

  /**
   * Get all blocked cells (for debugging)
   * @returns {Array<{x: number, z: number}>}
   */
  getBlockedCells() {
    const blocked = [];
    for (let cx = 0; cx < this.width; cx++) {
      for (let cz = 0; cz < this.height; cz++) {
        if (!this.cells[cx][cz]) {
          blocked.push({ x: cx, z: cz });
        }
      }
    }
    return blocked;
  }

  /**
   * Check if a world position is within this grid's bounds
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {boolean}
   */
  containsPoint(worldX, worldZ) {
    return worldX >= this.minX && worldX <= this.maxX &&
           worldZ >= this.minZ && worldZ <= this.maxZ;
  }

  /**
   * Find the nearest walkable cell to a world position
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {{x: number, z: number}|null} Nearest walkable cell or null
   */
  findNearestWalkableCell(worldX, worldZ) {
    const cell = this.worldToCell(worldX, worldZ);

    // If the target cell is walkable, return it
    if (this.isWalkable(cell.x, cell.z)) {
      return cell;
    }

    // BFS to find nearest walkable cell with iteration limit to prevent blocking
    const MAX_BFS_ITERATIONS = 500;
    const visited = new Set();
    const queue = [cell];
    visited.add(`${cell.x},${cell.z}`);

    let iterations = 0;
    while (queue.length > 0) {
      // Prevent blocking on large grids with few/no walkable cells
      if (++iterations > MAX_BFS_ITERATIONS) {
        return null;
      }

      const current = queue.shift();

      // Check all 8 neighbors
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          if (dx === 0 && dz === 0) continue;

          const nx = current.x + dx;
          const nz = current.z + dz;
          const key = `${nx},${nz}`;

          if (visited.has(key)) continue;
          if (!this.inBounds(nx, nz)) continue;

          visited.add(key);

          if (this.isWalkable(nx, nz)) {
            return { x: nx, z: nz };
          }

          queue.push({ x: nx, z: nz });
        }
      }
    }

    return null;
  }
}
